import { AutoField, useField } from "@w3rone/json-schema-form"
import type {
  FieldComponentProps,
  CollectionFieldSchema,
} from "@w3rone/json-schema-form"
import { FormErrors } from "./FormErrors"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import { getInitialValue } from "../getInitialValue"
import AddBox from "@mui/icons-material/AddBox"
import DeleteForever from "@mui/icons-material/DeleteForever"
import Info from "@mui/icons-material/Info"

export const CollectionGridField = ({ name }: FieldComponentProps) => {
  const {
    value,
    schema: schemaProps,
    onChange,
    label,
    description,
    errors,
  } = useField(name)

  assertCollectionValue(value)

  const schema = schemaProps as CollectionFieldSchema

  const handleAdd = () => {
    const newValue = [
      ...(value || []),
      Array.isArray(schema.items)
        ? undefined
        : getInitialValue(schema.items, null),
    ]

    onChange?.(newValue)
  }

  const handleRemove = (index: number) => {
    const defaultedValue = value || []

    onChange?.([
      ...defaultedValue.slice(0, index),
      undefined,
      ...defaultedValue.slice(index + 1),
    ])
  }

  return (
    <FormControl
      variant="standard"
      component="fieldset"
      error={errors.length > 0}
      fullWidth={true}
    >
      {label ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormLabel component="legend">{label}</FormLabel>
          {description ? (
            <Tooltip
              title={<div dangerouslySetInnerHTML={{ __html: description }} />}
            >
              <Info />
            </Tooltip>
          ) : null}
        </Box>
      ) : null}
      <FormErrors errors={errors} />

      {schema.options.collection.allowAdd && !schema.readOnly ? (
        <Button startIcon={<AddBox />} type="button" onClick={handleAdd}>
          Ajouter un élément
        </Button>
      ) : null}

      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}
      >
        {Array.isArray(value)
          ? value.map((itemValue, index) => {
              const itemName = `${name}[${index}]`

              if (itemValue === undefined) {
                return null
              }

              return (
                <Box
                  key={itemName}
                  sx={{ display: "flex", gap: 3, alignItems: "center" }}
                >
                  <AutoField name={itemName} />
                  {schema.options.collection.allowDelete && !schema.readOnly ? (
                    <IconButton
                      aria-label="Supprimer"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemove(index)
                      }}
                      edge="start"
                    >
                      <DeleteForever />
                    </IconButton>
                  ) : null}
                </Box>
              )
            })
          : null}
        {Array.isArray(value) &&
        value.length > 0 &&
        schema.options.collection.allowAdd &&
        !schema.readOnly ? (
          <Button startIcon={<AddBox />} type="button" onClick={handleAdd}>
            Ajouter un élément
          </Button>
        ) : null}
      </Box>
    </FormControl>
  )
}

function assertCollectionValue(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): asserts value is any[] | undefined {
  if (value && !Array.isArray(value)) {
    throw new Error("value is not a collection value")
  }
}
